import React, { useEffect } from "react";
import { Box, IconButton, Image, Text, VStack, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import HomeIcon from "@mui/icons-material/Home";
import Grid from "../components/Grid";
import HelpModal from "../components/HelpModal";
import OutOfTimeModal from "../components/OutOfTimeModal";
import { BackgroundType, ColourPoint, GameMode, GridBoxPath, Point, SpecialTile, iconColors, backgroundStyles, allStageEffects, levelTimerStart } from "../types";
import { WaveBackground } from "../images/waveBackground";
import { ForestBackground } from "../images/forestBackground";
import { DesertBackground } from "../images/desertBackground";
import { PortalBackground } from "../images/portalBackground";
import { HauntedHouseBackground } from "../images/hauntedHouseBackground";
import { VaporwaveCityBackground } from "../images/vaporwaveCityBackground";
import { AlienVillageBackground } from "../images/alienVillageBackground";
import { PaintSplatBackground } from "../images/paintSplatBackground";
import { MagicWorldBackground } from "../images/magicWorldBackground";
import { JungleBackground } from "../images/jungleBackground";
import { TechnologyBackground } from "../images/technologyBackground";
import { MagicPortalBackground } from "../images/magicPortalBackground";
import { WarehouseBackground } from "../images/warehouseBackground";
import { onNewPuzzle } from "../utils/onNewPuzzle";

type GamePageProps = {
  puzzle: {
    circles: ColourPoint[];
    size: number;
    wallTiles: Point[];
    specialTiles: SpecialTile[];
    stageEffects: string[];
    colorCount?: number;
    backgroundType?: BackgroundType;
  };
  setPuzzle: React.Dispatch<React.SetStateAction<{
    circles: ColourPoint[];
    size: number;
    wallTiles: Point[];
    specialTiles: SpecialTile[];
    stageEffects: string[];
    colorCount?: number;
    backgroundType?: BackgroundType;
  }>>;
  path: { [key: string]: GridBoxPath };
  setPath: React.Dispatch<React.SetStateAction<{ [key: string]: GridBoxPath }>>;
  completedPaths: { [key: string]: boolean };
  setCompletedPaths: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  levelNumber: number;
  setLevelNumber: React.Dispatch<React.SetStateAction<number>>;
  level: number;
  setLevel: React.Dispatch<React.SetStateAction<number>>;
  size: number;
  setSize: React.Dispatch<React.SetStateAction<number>>;
  colourCount: number;
  setColourCount: React.Dispatch<React.SetStateAction<number>>;
  unlockedStageTypes: string[];
  setUnlockedStageTypes: React.Dispatch<React.SetStateAction<string[]>>;
  gameMode: GameMode;
  setGameMode: React.Dispatch<React.SetStateAction<GameMode>>;
  bombTimer: number;
  setBombTimer: React.Dispatch<React.SetStateAction<number>>;
  levelTimer: number;
  setLevelTimer: React.Dispatch<React.SetStateAction<number>>;
  worldNumber: number;
  setWorldNumber: React.Dispatch<React.SetStateAction<number>>;
  worldLevelNumber: number;
  setWorldLevelNumber: React.Dispatch<React.SetStateAction<number>>;
  selectedWorldId: number;
  setSelectedWorldId: React.Dispatch<React.SetStateAction<number>>;
  numberOfConnectedColors: number;
  setNumberOfConnectedColors: React.Dispatch<React.SetStateAction<number>>;
  showPopup: boolean;
  popupText: string;
  popupColor: string;
  triggerPopup: (text: string, color?: string) => void;
  beginNewPuzzle: (mode?: GameMode, worldId?: number) => void;
  isOutOfTime: boolean;
  onOutOfTimeClose: () => void;
};

const GamePage: React.FC<GamePageProps> = ({
  puzzle,
  setPuzzle,
  path,
  setPath,
  completedPaths,
  setCompletedPaths,
  levelNumber,
  setLevelNumber,
  level,
  setLevel,
  size,
  setSize,
  colourCount,
  setColourCount,
  unlockedStageTypes,
  setUnlockedStageTypes,
  gameMode,
  setGameMode,
  bombTimer,
  setBombTimer,
  levelTimer,
  setLevelTimer,
  worldNumber,
  setWorldNumber,
  worldLevelNumber,
  setWorldLevelNumber,
  selectedWorldId,
  setSelectedWorldId,
  numberOfConnectedColors,
  setNumberOfConnectedColors,
  showPopup,
  popupText,
  popupColor,
  triggerPopup,
  beginNewPuzzle,
  isOutOfTime,
  onOutOfTimeClose,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isOpen: isHelpModalOpen,
    onOpen: onHelpModalOpen,
    onClose: onHelpModalClose,
  } = useDisclosure();

  // On mount/refresh, check URL params and reinitialize game if needed
  useEffect(() => {
    const modeParam = searchParams.get("mode") as GameMode | null;
    const timedParam = searchParams.get("timed") === "true";
    const worldIdParam = searchParams.get("worldId");

    // Only reinitialize if we have URL params and puzzle is empty (page was refreshed)
    if (modeParam && puzzle.circles.length === 0) {
      const worldId = worldIdParam ? parseInt(worldIdParam, 10) : undefined;

      // Set up game mode
      setGameMode(modeParam);

      // Set up timed mode if specified
      if (timedParam) {
        setLevelTimer(levelTimerStart);
      }

      // Set up unlocked effects based on mode
      if (modeParam === GameMode.endless) {
        setUnlockedStageTypes(allStageEffects);
      }

      // Start the game with appropriate mode and worldId
      beginNewPuzzle(modeParam, worldId);
    }
  }, []); // Only run once on mount

  const handleReturnToTitle = () => {
    navigate("/");
  };

  const handleSkip = () => {
    onNewPuzzle({
      setPath,
      setCompletedPaths,
      setNumberOfConnectedColors,
      level,
      size,
      colourCount,
      setLevel,
      setSize,
      setColourCount,
      setPuzzle,
      unlockedStageTypes,
      setUnlockedStageTypes,
      triggerPopup,
      levelNumber,
      setLevelNumber,
      gameMode,
      setGameMode,
      setBombTimer,
      levelTimer,
      setLevelTimer,
      worldNumber,
      setWorldNumber,
      worldLevelNumber,
      setWorldLevelNumber,
      selectedWorldId,
    });
  };

  // Get the background style based on backgroundType
  const currentBackground = puzzle.backgroundType || BackgroundType.solidBlack;
  const isAnimatedBackground = currentBackground === BackgroundType.calmWaves || currentBackground === BackgroundType.forestCanopy || currentBackground === BackgroundType.desertDunes || currentBackground === BackgroundType.portalRealm || currentBackground === BackgroundType.hauntedHouse || currentBackground === BackgroundType.vaporwaveCity || currentBackground === BackgroundType.alienVillage || currentBackground === BackgroundType.paintSplat || currentBackground === BackgroundType.magicWorld || currentBackground === BackgroundType.jungle || currentBackground === BackgroundType.technology || currentBackground === BackgroundType.magicPortal || currentBackground === BackgroundType.warehouse;
  const backgroundColor = isAnimatedBackground
    ? "transparent"
    : backgroundStyles[currentBackground] || "black";

  return (
    <VStack>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        backgroundColor={backgroundColor}
        h="100vh"
        w="100vw"
        position="relative"
      >
        {/* Render animated backgrounds based on type */}
        {currentBackground === BackgroundType.calmWaves && <WaveBackground />}
        {currentBackground === BackgroundType.forestCanopy && <ForestBackground />}
        {currentBackground === BackgroundType.desertDunes && <DesertBackground />}
        {currentBackground === BackgroundType.portalRealm && <PortalBackground />}
        {currentBackground === BackgroundType.hauntedHouse && <HauntedHouseBackground />}
        {currentBackground === BackgroundType.vaporwaveCity && <VaporwaveCityBackground />}
        {currentBackground === BackgroundType.alienVillage && <AlienVillageBackground />}
        {currentBackground === BackgroundType.paintSplat && <PaintSplatBackground />}
        {currentBackground === BackgroundType.magicWorld && <MagicWorldBackground />}
        {currentBackground === BackgroundType.jungle && <JungleBackground />}
        {currentBackground === BackgroundType.technology && <TechnologyBackground />}
        {currentBackground === BackgroundType.magicPortal && <MagicPortalBackground />}
        {currentBackground === BackgroundType.warehouse && <WarehouseBackground />}
        <Grid
          size={puzzle.size}
          completedPaths={completedPaths}
          setCompletedPaths={setCompletedPaths}
          circles={puzzle.circles}
          path={path}
          setPath={setPath}
          wallTiles={puzzle.wallTiles}
          specialTiles={puzzle.specialTiles}
          setPuzzle={setPuzzle}
          stageEffects={puzzle.stageEffects}
          isHelpModalOpen={isHelpModalOpen}
          bombTimer={bombTimer}
          setBombTimer={setBombTimer}
        />
      </Box>
      <Text
        fontSize="6xl"
        fontFamily="monospace"
        color="white"
        position="absolute"
        top="3%"
        left="50%"
        transform="translate(-50%, 0)"
      >
        {levelNumber}
      </Text>
      {levelTimer > 0 && (
        <Box
          position="absolute"
          bottom="0%"
          left="50%"
          transform="translateX(-50%)"
          w="100px"
          h="100px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          color="tomato"
        >
          <Image
            src="Bomb.png"
            w="70%"
            h="70%"
            position="absolute"
            zIndex="3"
            top="50%"
            left="50%"
            transform="translateY(-50%) translateX(-50%)"
          />
          <Box
            position="absolute"
            w="70%"
            h="70%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex="4"
            textColor="white"
            fontWeight="bold"
            top="55%"
            left="50%"
            transform="translateY(-50%) translateX(-50%)"
          >
            {levelTimer}
          </Box>
        </Box>
      )}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "20px",
            backgroundColor: popupColor,
            color: iconColors[popupColor as keyof typeof iconColors] || "white",
            borderRadius: "10px",
            animation: "grow 1s ease-in-out",
            fontFamily: "monospace",
            fontSize: "4rem",
            zIndex: 100,
          }}
        >
          {popupText}
        </div>
      )}
      <style>
        {`
          @keyframes grow {
            0% {
              transform: translate(-50%, -50%) scale(0.5);
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
            }
          }
        `}
      </style>
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={onHelpModalClose}
        unlockedStageTypes={unlockedStageTypes}
        levelNumber={levelNumber}
      />
      <OutOfTimeModal
        isOpen={isOutOfTime}
        onClose={onOutOfTimeClose}
        levelNumber={levelNumber}
        setGameMode={setGameMode}
        setUnlockedStageTypes={setUnlockedStageTypes}
        setLevelTimer={setLevelTimer}
        beginNewPuzzle={beginNewPuzzle}
        selectedWorldId={selectedWorldId}
        setSelectedWorldId={setSelectedWorldId}
        onReturnToTitle={handleReturnToTitle}
      />
      <IconButton
        aria-label="Help"
        colorScheme="blue"
        icon={<HelpCenterIcon />}
        position="absolute"
        bottom="3%"
        right="3%"
        onClick={onHelpModalOpen}
      />
      <IconButton
        aria-label="Home"
        colorScheme="gray"
        icon={<HomeIcon />}
        position="absolute"
        top="3%"
        left="3%"
        onClick={handleReturnToTitle}
      />
      {window.location.hostname === "localhost" && (
        <IconButton
          aria-label="Skip"
          colorScheme="blue"
          icon={<SkipNextIcon />}
          position="absolute"
          bottom="3%"
          left="3%"
          onClick={handleSkip}
        />
      )}
    </VStack>
  );
};

export default GamePage;
